// Mote theme: variant picker, quantity, AJAX cart drawer, sticky bar, parts viewer, entrances.
// Everything here enhances plain HTML that already works without JavaScript.
(() => {
  const routes = window.theme.routes;
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];
  const drawer = () => $('[data-cart-drawer]');

  /* Cart drawer */
  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = Number(count) === 0;
    });
    $$('[data-cart-open]').forEach((el) => {
      el.setAttribute('aria-label', `Cart, ${count} ${Number(count) === 1 ? 'item' : 'items'}`);
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  };

  const refreshCart = async () => {
    const res = await fetch(`${routes.root.replace(/\/$/, '')}/?section_id=cart-drawer`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const next = $('[data-cart-inner]', doc);
    const current = $('[data-cart-inner]');
    if (!next || !current) return;
    current.replaceWith(next);
    updateCount(next.dataset.count);
  };

  const openDrawer = () => {
    const d = drawer();
    if (!d) return false;
    if (!d.open) d.showModal();
    $('#cart-drawer-title', d)?.focus();
    return true;
  };

  document.addEventListener('click', async (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && openDrawer()) {
      e.preventDefault();
      return;
    }

    const closer = e.target.closest('[data-cart-close]');
    if (closer) {
      drawer()?.close();
      return; // links still follow their href (e.g. back to #buy)
    }

    // Click on the dimmed backdrop closes the drawer.
    if (e.target === drawer()) {
      drawer().close();
      return;
    }

    const change = e.target.closest('[data-cart-change]');
    if (change && drawer()?.contains(change)) {
      e.preventDefault();
      const lines = change.closest('.lines');
      lines?.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${routes.change}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(change.dataset.line), quantity: Number(change.dataset.qty) }),
        });
        if (!res.ok) throw new Error();
        await refreshCart();
        $('#cart-drawer-title')?.focus();
      } catch {
        window.location.href = change.href; // fall back to the plain link
      }
    }
  });

  /* Buy links jump to the purchase box; if this page has none (section removed), open the product page instead. */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-buy-link]');
    if (!link || link.getAttribute('href') !== '#buy' || document.getElementById('buy')) return;
    if (link.dataset.productUrl) {
      e.preventDefault();
      window.location.href = `${link.dataset.productUrl}#buy`;
    }
  });

  /* Quantity steppers */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const input = btn.parentElement.querySelector('input');
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.qtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Product sections: variants, add to cart, sticky bar */
  $$('[data-product-section]').forEach((section) => {
    const form = $('form[data-product-form]', section);
    if (!form) return;
    const variants = JSON.parse($('[data-variants]', section).textContent);
    const idInput = $('[data-variant-id]', form);
    const fieldsets = $$('[data-option]', section);
    const status = $('[data-form-status]', form);
    const addButtons = () => [...form.elements].filter((el) => el.matches('[data-add]'));
    const sticky = $('[data-sticky]');
    const img = $('.buy__img', section);
    let current = variants.find((v) => String(v.id) === idInput.value) || null;

    const setButtons = (v, busy = false) => {
      addButtons().forEach((b) => {
        b.disabled = busy || !v || !v.available;
        b.setAttribute('aria-busy', String(busy));
        b.textContent = busy ? 'Adding…' : !v ? b.dataset.labelNone : v.available ? b.dataset.labelAdd : b.dataset.labelSold;
      });
    };

    const selected = () => fieldsets.map((fs) => $('input:checked', fs)?.value);
    const match = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

    const onOptionChange = () => {
      const opts = selected();
      current = match(opts) || null;
      // Strike through values that are sold out or don't exist with the other current choices.
      fieldsets.forEach((fs, i) => {
        $$('input', fs).forEach((input) => {
          const test = [...opts];
          test[i] = input.value;
          const v = match(test);
          input.closest('.chip').classList.toggle('is-unavailable', !v || !v.available);
        });
      });
      idInput.value = current ? current.id : '';
      $$('[data-price]', section).concat(sticky ? $$('[data-price]', sticky) : []).forEach((el) => {
        if (current) el.innerHTML = current.price;
      });
      if (sticky) $('[data-sticky-variant]', sticky).textContent = current ? current.title : '';
      if (current?.image && img) {
        img.removeAttribute('srcset');
        img.src = current.image;
      }
      setButtons(current);
      status.textContent = '';
      if (current) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', current.id);
        history.replaceState(history.state, '', url);
      }
    };
    fieldsets.forEach((fs) => fs.addEventListener('change', onOptionChange));
    if (fieldsets.length) onOptionChange();

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!current?.available) return;
      setButtons(current, true);
      status.classList.remove('is-error');
      try {
        const res = await fetch(`${routes.add}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const data = await res.json();
        if (!res.ok) {
          status.classList.add('is-error');
          status.textContent = data.description || data.message || 'Could not add to cart. Please try again.';
          return;
        }
        await refreshCart();
        status.textContent = 'Added to cart.';
        openDrawer();
      } catch {
        status.classList.add('is-error');
        status.textContent = 'Could not add to cart. Check your connection and try again.';
      } finally {
        setButtons(current);
      }
    });

    // Sticky bar: shown only while no other way to buy is on screen
    // (hero button, purchase card, closing band), and never over the footer.
    if (sticky && 'IntersectionObserver' in window) {
      const watched = [$('[data-buy-controls]', section), ...$$('[data-hero], .band-wrap, .footer')].filter(Boolean);
      const seen = new Map(watched.map((el) => [el, true]));
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => seen.set(en.target, en.isIntersecting));
        const show = ![...seen.values()].some(Boolean);
        sticky.classList.toggle('is-shown', show);
        sticky.inert = !show;
      });
      watched.forEach((el) => io.observe(el));
    }
  });

  /* Parts viewer: tap a part, the photo zooms to it. */
  $$('[data-parts]').forEach((section) => {
    const frame = $('[data-parts-frame]', section);
    const caption = $('[data-parts-caption]', section);
    const initial = caption?.textContent;
    $$('[data-part]', section).forEach((btn) => {
      btn.addEventListener('click', () => {
        const on = btn.getAttribute('aria-pressed') !== 'true';
        $$('[data-part]', section).forEach((b) => b.setAttribute('aria-pressed', String(b === btn && on)));
        frame.style.setProperty('--x', on ? btn.dataset.x : frame.dataset.x);
        frame.style.setProperty('--y', on ? btn.dataset.y : frame.dataset.y);
        frame.style.setProperty('--z', on ? btn.dataset.z : frame.dataset.z);
        if (caption) caption.textContent = on ? btn.dataset.caption : initial;
      });
    });
  });

  /* Entrances */
  if ('IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.documentElement.classList.add('motion');
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('in');
        io.unobserve(en.target);
      }),
      { rootMargin: '0px 0px -8% 0px' }
    );
    $$('[data-reveal]').forEach((el) => io.observe(el));
  }
})();
